import type {
  FieldFunctionOptions,
  InMemoryCacheConfig,
  Reference,
  StoreObject,
} from '@apollo/client'

import possibleTypes from 'src/graphql/possibleTypes'

const getTaskCacheId = (
  task: Reference | StoreObject,
  readField: FieldFunctionOptions['readField']
) =>
  readField<string>('id', task) ??
  (typeof task === 'object' && 'id' in task ? task.id : undefined)

const isIncomingTasksPrefixOfExistingTasks = ({
  existingTasks,
  incomingTasks,
  readField,
}: {
  existingTasks: Array<Reference | StoreObject>
  incomingTasks: Array<Reference | StoreObject>
  readField: FieldFunctionOptions['readField']
}) =>
  incomingTasks.every((incomingTask, index) => {
    const existingTask = existingTasks[index]

    return (
      existingTask &&
      getTaskCacheId(existingTask, readField) ===
        getTaskCacheId(incomingTask, readField)
    )
  })

export const cacheConfig: InMemoryCacheConfig = {
  ...possibleTypes,
  typePolicies: {
    // Vitals set measurements have no id of their own, so Apollo stores them inside their
    // VitalsSet. Merge rather than replace so queries selecting different fields share them.
    VitalsSetBloodPressure: { merge: true },
    VitalsSetBmi: { merge: true },
    VitalsSetHeadCircumference: { merge: true },
    VitalsSetHeight: { merge: true },
    VitalsSetTemperature: { merge: true },
    VitalsSetWeight: { merge: true },
    WeightForStature: { merge: true },
    // Have no id either, so they live inside their Patient.
    LatestVitals: { merge: true },
    BirthHistoryVitals: { merge: true },
    BirthHistoryWeight: { merge: true },
    BirthHistoryLength: { merge: true },
    BirthHistoryValue: { merge: true },
    BirthHistoryDischargeWeight: { merge: true },
    PatientScreeningTask: { keyFields: false },
    Query: {
      fields: {
        tasks: {
          keyArgs: (args) =>
            JSON.stringify({
              filters: args?.['input']?.filters,
              sort: args?.['input']?.sort,
            }),
          merge(existing, incoming, { args, readField }) {
            if (!args?.['input']?.paginationInput?.after) {
              if (
                existing?.tasks?.length > incoming.tasks.length &&
                isIncomingTasksPrefixOfExistingTasks({
                  existingTasks: existing.tasks,
                  incomingTasks: incoming.tasks,
                  readField,
                })
              ) {
                return existing
              }

              return incoming
            }

            const mergedTasks = existing?.tasks ? [...existing.tasks] : []
            const seenTaskIds = new Set(
              mergedTasks.map((task) => getTaskCacheId(task, readField))
            )

            for (const task of incoming.tasks) {
              const taskId = getTaskCacheId(task, readField)

              if (!taskId || !seenTaskIds.has(taskId)) {
                mergedTasks.push(task)
              }

              if (taskId) {
                seenTaskIds.add(taskId)
              }
            }

            return {
              ...incoming,
              tasks: mergedTasks,
            }
          },
        },
      },
    },
  },
}
